import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { SheetView } from '../Sheet/SheetView';
import { SheetBar } from '../Sheet/SheetBar';
import { SheetTypeDialog } from '../Sheet/SheetTypeDialog';
import { AddSheetDialog } from '../Sheet/AddSheetDialog';
import { WorkbookNav } from './WorkbookNav';
import { WorkbookSidebar } from './WorkbookSidebarView';
import { NewFileView } from './WorkbookNewFileView';
import { useStores } from '../../stores/RootStore';
import { SheetViewModel } from '../../viewModels/SheetViewModel';
import { TemplateModel } from '../../models/TemplateModel';

export const WorkbookView = observer(({ viewModel }) => {
    const { sheetStore, fileSystemStore, settingsStore } = useStores();
    
    const sheetVM = useMemo(() => {
        if (!sheetStore || !viewModel.currentWorkbook?.id) return null;
        console.log("Re-creating SheetViewModel for workbook:", viewModel.currentWorkbook.id);
        return new SheetViewModel({ sheetStore });
    }, [sheetStore, viewModel.currentWorkbook?.id]);

    const currentWorkbook = viewModel.currentWorkbook;
    const showSheetSelector = currentWorkbook && !currentWorkbook.isCSV && !viewModel.isNewFileViewOpen;
    const canShowSheetTypeDialog = showSheetSelector && sheetVM?.currentSheet;

    useEffect(() => {
        const handleKeyboardShortcuts = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 's') {
                event.preventDefault();
                if (viewModel.currentWorkbook && !viewModel.isNewFileViewOpen) {
                    if (event.shiftKey) {
                        console.log("Shortcut: Save As");
                        viewModel.handleSaveAs();
                    } else {
                        console.log("Shortcut: Save");
                        viewModel.handleSave();
                    }
                } else {
                    console.log("Save shortcut ignored: No workbook open or in New File view.");
                }
            }

            if ((event.metaKey || event.ctrlKey) && event.key === '\\') {
                event.preventDefault();
                viewModel.toggleSidebar();
            }
        };

        window.addEventListener('keydown', handleKeyboardShortcuts);
        return () => window.removeEventListener('keydown', handleKeyboardShortcuts);
    }, [viewModel]);

    if (currentWorkbook && !sheetVM && !viewModel.isNewFileViewOpen) {
        return (
            <div className="flex h-screen overflow-hidden">
                <WorkbookSidebar
                    viewModel={viewModel}
                    isExpanded={viewModel.isSidebarExpanded}
                    toggleSidebar={viewModel.toggleSidebar}
                    onCreateNew={viewModel.openNewFileView}
                    onOpenFile={viewModel.handleOpenFileFromSidebar}
                    onOpenFolder={viewModel.handleRequestDirectoryAccessFromSidebar}
                    onOpenRecent={viewModel.handleOpenRecentFile}
                    onOpenFromDirectory={viewModel.openFileFromDirectory}
                    fileSystemStore={fileSystemStore}
                    settingsStore={settingsStore}
                />
                <div className="flex-grow flex items-center justify-center bg-white">
                    Loading Sheet View...
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen overflow-hidden">
            <WorkbookSidebar
                viewModel={viewModel}
                isExpanded={viewModel.isSidebarExpanded}
                toggleSidebar={viewModel.toggleSidebar}
                onCreateNew={viewModel.openNewFileView}
                onOpenFile={viewModel.handleOpenFileFromSidebar}
                onOpenFolder={viewModel.handleRequestDirectoryAccessFromSidebar}
                onOpenRecent={viewModel.handleOpenRecentFile}
                onOpenFromDirectory={viewModel.openFileFromDirectory}
                fileSystemStore={fileSystemStore}
                settingsStore={settingsStore}
            />

            <div className="flex-grow flex flex-col bg-white overflow-hidden">
                {viewModel.isNewFileViewOpen ? (
                    <NewFileView
                        onCreateBlank={viewModel.handleCreateBlankWorkbook}
                        onSelectTemplate={viewModel.handleSelectTemplate}
                        onCreateFromViewType={viewModel.handleCreateWorkbookFromViewType}
                    />
                ) : !currentWorkbook ? (
                    <div className="flex items-center justify-center h-full text-gray-500 text-lg p-8 text-center">
                        Use the sidebar to create a new workbook or open an existing file.
                    </div>
                ) : (
                    <>
                        <WorkbookNav
                            workbookName={currentWorkbook?.properties?.title ?? 'Loading...'}
                            isSaved={viewModel.isSaved}
                            isSaving={viewModel.isSaving}
                            onSave={viewModel.handleSave}
                            onSaveAs={viewModel.handleSaveAs}
                            onSaveAsCSV={viewModel.handleSaveAsCSV}
                            view={viewModel.view}
                            setView={viewModel.setView}
                            hasEditPermission={viewModel.hasEditPermission}
                            onNameChange={viewModel.updateWorkbookName}
                            isCSV={currentWorkbook?.isCSV ?? false}
                            isNewWorkbook={currentWorkbook?.isNewWorkbook ?? false}
                        />

                        <div className="flex-grow flex flex-col overflow-hidden">
                            {showSheetSelector && sheetVM && (
                                <SheetBar
                                    sheetVM={sheetVM}
                                    onAddSheet={sheetVM.showAddSheetModal}
                                    maxSheets={sheetVM.MAX_SHEETS}
                                    onSelectSheet={sheetVM.selectSheet}
                                    onRenameSheet={sheetVM.renameSheet}
                                    onDeleteSheet={sheetVM.deleteSheet}
                                />
                            )}

                            <div className="flex-grow relative overflow-auto">
                                {sheetVM?.currentSheet ? (
                                    <SheetView
                                        key={sheetVM.currentSheet.id}
                                        sheet={sheetVM.currentSheet}
                                        sheetData={sheetVM.currentSheetData}
                                        view={viewModel.view}
                                        sheetInteraction={sheetVM.sheetInteraction}
                                        onUpdate={(update) => {
                                            if (update.type === 'select_type') {
                                                viewModel.openSheetTypeDialog();
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-500">
                                        {currentWorkbook?.sheets?.length > 0 ? 'Select a sheet' : 'This workbook has no sheets.'}
                                    </div>
                                )}
                            </div>
                        </div>

                        {canShowSheetTypeDialog && sheetVM && (
                            <SheetTypeDialog
                                isOpen={viewModel.isSheetTypeDialogOpen}
                                onClose={() => viewModel.closeSheetTypeDialog()}
                                onSelect={(type) => {
                                    sheetVM.handleSheetTypeSelection(type);
                                    viewModel.closeSheetTypeDialog();
                                }}
                            />
                        )}

                        {sheetVM && (
                            <AddSheetDialog
                                isOpen={sheetVM.isAddSheetModalOpen}
                                onClose={sheetVM.hideAddSheetModal}
                                onSelectOption={sheetVM.handleAddSheetSelection}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
});